import type { GrantString } from "@/lib/permissions";
import { BaseProvider } from "./base.provider";

export interface SpecialUser {
  user_id: string;
  email?: string;
  note?: string;
  // Raw grant strings. May hold hand-edited junk, so never cast to GrantString.
  permissions: string[];
  ratelimit_bypass: boolean;
  created_at: string;
  updated_at: string;
}

export interface UpsertSpecialUserPayload {
  permissions: GrantString[]; // full replace
  ratelimit_bypass: boolean; // required, even when false
  email?: string;
  note?: string;
}

export interface PermissionsTemplate {
  permissions: GrantString[]; // every area at ":none"
  ratelimit_bypass: boolean;
}

// The API promises an array and never null, but the old backend didn't send
// the field at all — tolerate both while the two deploys overlap.
function normalize(grant: SpecialUser): SpecialUser {
  return { ...grant, permissions: grant.permissions ?? [] };
}

class SpecialUsersProvider extends BaseProvider {
  async list(): Promise<SpecialUser[]> {
    const grants = await this.request<SpecialUser[]>("/v1/admin/special-users");
    return grants.map(normalize);
  }

  // 404 means the user simply has no grant — a normal answer, not an error.
  async getById(userId: string): Promise<SpecialUser | null> {
    const grant = await this.requestOrNullOn404<SpecialUser>(
      `/v1/admin/special-users/${encodeURIComponent(userId)}`,
    );
    return grant && normalize(grant);
  }

  // Full replace — the whole permissions array must be sent every time, or the
  // omitted entries are revoked.
  async upsert(
    userId: string,
    payload: UpsertSpecialUserPayload,
  ): Promise<SpecialUser> {
    return normalize(
      await this.request<SpecialUser>(
        `/v1/admin/special-users/${encodeURIComponent(userId)}`,
        { method: "PUT", body: JSON.stringify(payload) },
      ),
    );
  }

  // Every area at "none" — the starting point for a new grant.
  getTemplate(): Promise<PermissionsTemplate> {
    return this.request<PermissionsTemplate>("/v1/admin/permissions/template");
  }

  async revoke(userId: string): Promise<void> {
    await this.requestOrNullOn404<void>(
      `/v1/admin/special-users/${encodeURIComponent(userId)}`,
      { method: "DELETE" },
    );
  }
}

export const specialUsersProvider = new SpecialUsersProvider();
